import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmail,
  signInWithGoogle as firebaseSignInWithGoogle,
  registerWithEmailAndPassword,
  resetPassword,
  logOut as firebaseLogOut,
  subscribeToAuthChanges,
  parseAuthError
} from '../services/firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { getRedirectResult } from 'firebase/auth';
import { db, auth } from '../services/firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Secure token management with AES-GCM encryption (Web Crypto API)
class SecureTokenManager {
  constructor() {
    this.tokenKey = process.env.REACT_APP_TOKEN_KEY;
    this.refreshTokenKey = `${process.env.REACT_APP_TOKEN_KEY}_refresh`;
    this.sessionKey = `${process.env.REACT_APP_TOKEN_KEY}_session`;
    this.expiryKey = `${process.env.REACT_APP_TOKEN_KEY}_expiry`;
    this.SESSION_DURATION = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    this.SALT = 'ekah_health_secure_salt_2024'; // Salt for key derivation
  }

  // Generate a secure session ID
  generateSessionId() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  // Derive a cryptographic key from password using PBKDF2 with salt
  async deriveKey(password) {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    const salt = encoder.encode(this.SALT);

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000, // High iterations for security
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  // Encrypt sensitive data using AES-GCM
  async encrypt(data, password) {
    try {
      const key = await this.deriveKey(password);
      const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for AES-GCM
      const encoder = new TextEncoder();

      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encoder.encode(data)
      );

      // Combine IV + encrypted data and encode as base64
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('AES-GCM encryption failed:', error);
      throw error;
    }
  }

  // Decrypt sensitive data using AES-GCM
  async decrypt(encryptedData, password) {
    try {
      const key = await this.deriveKey(password);
      const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

      // Extract IV (first 12 bytes) and encrypted data
      const iv = combined.slice(0, 12);
      const data = combined.slice(12);

      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        data
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.error('AES-GCM decryption failed:', error);
      return null;
    }
  }

  // Check if session has expired
  isSessionExpired() {
    const expiryTime = sessionStorage.getItem(this.expiryKey);
    if (!expiryTime) return true;
    return Date.now() > parseInt(expiryTime, 10);
  }

  // Store token securely (async)
  async storeToken(token, refreshToken = null) {
    try {
      const sessionId = this.generateSessionId();
      const encryptionKey = `ekah_${sessionId}_user`;

      // Encrypt the token using AES-GCM
      const encryptedToken = await this.encrypt(token, encryptionKey);

      // Calculate expiry time (2 hours from now)
      const expiryTime = Date.now() + this.SESSION_DURATION;

      // Store in sessionStorage with session validation and expiry
      sessionStorage.setItem(this.tokenKey, encryptedToken);
      sessionStorage.setItem(this.sessionKey, sessionId);
      sessionStorage.setItem(this.expiryKey, expiryTime.toString());

      if (refreshToken) {
        const encryptedRefresh = await this.encrypt(refreshToken, encryptionKey);
        sessionStorage.setItem(this.refreshTokenKey, encryptedRefresh);
      }

      return true;
    } catch (error) {
      console.error('Token storage failed:', error);
      return false;
    }
  }

  // Retrieve token securely (async)
  async getToken() {
    try {
      // Check if session has expired
      if (this.isSessionExpired()) {
        this.clearTokens();
        return null;
      }

      const encryptedToken = sessionStorage.getItem(this.tokenKey);
      const sessionId = sessionStorage.getItem(this.sessionKey);

      if (!encryptedToken || !sessionId) {
        return null;
      }

      const encryptionKey = `ekah_${sessionId}_user`;
      const token = await this.decrypt(encryptedToken, encryptionKey);

      if (!token) {
        this.clearTokens();
        return null;
      }

      return token;
    } catch (error) {
      console.error('Token retrieval failed:', error);
      this.clearTokens();
      return null;
    }
  }

  // Clear all tokens
  clearTokens() {
    try {
      sessionStorage.removeItem(this.tokenKey);
      sessionStorage.removeItem(this.refreshTokenKey);
      sessionStorage.removeItem(this.sessionKey);
      sessionStorage.removeItem(this.expiryKey);

      // Clear any session cookies
      document.cookie = 'user_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict; Secure';
    } catch (error) {
      console.error('Token cleanup failed:', error);
    }
  }

  // Get refresh token (async)
  async getRefreshToken() {
    try {
      // Check if session has expired
      if (this.isSessionExpired()) {
        this.clearTokens();
        return null;
      }

      const encryptedRefresh = sessionStorage.getItem(this.refreshTokenKey);
      const sessionId = sessionStorage.getItem(this.sessionKey);

      if (!encryptedRefresh || !sessionId) {
        return null;
      }

      const encryptionKey = `ekah_${sessionId}_user`;
      return await this.decrypt(encryptedRefresh, encryptionKey);
    } catch (error) {
      console.error('Refresh token retrieval failed:', error);
      return null;
    }
  }
}

// Create a single instance outside the component to avoid recreating on each render
const tokenManager = new SecureTokenManager();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [error, setError] = useState(null);

  // Initialize authentication state with Firebase
  useEffect(() => {
    // Check for Google Sign-in redirect result on app load
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          // User signed in via redirect, create/update user document
          const userDocRef = doc(db, 'users', result.user.uid);
          await setDoc(userDocRef, {
            email: result.user.email,
            name: result.user.displayName,
            photoURL: result.user.photoURL,
            role: 'user',
            lastLogin: new Date(),
            createdAt: new Date()
          }, { merge: true });
        }
      } catch (error) {
        console.error('Redirect result error:', error);
        setError(parseAuthError(error));
      }
    };

    checkRedirectResult();

    const unsubscribe = subscribeToAuthChanges(async (firebaseUser) => {
      try {
        setIsLoading(true);

        if (firebaseUser) {
          // Get additional user data from Firestore
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          let userData = {
            id: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            role: 'user'
          };

          // If user document exists in Firestore, merge the data
          if (userDoc.exists()) {
            userData = { ...userData, ...userDoc.data() };
          } else {
            // Create user document in Firestore for new users
            await setDoc(userDocRef, {
              email: firebaseUser.email,
              name: firebaseUser.displayName,
              role: 'user',
              createdAt: new Date(),
              lastLogin: new Date()
            });
          }

          // Check for admin credentials for admin login
          if (firebaseUser.email === process.env.REACT_APP_ADMIN_EMAIL) {
            userData.role = 'admin';
          }

          setUser(userData);
          setSessionInfo({
            loginTime: new Date(),
            lastActivity: new Date()
          });
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setSessionInfo(null);
          tokenManager.clearTokens();
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        setError(parseAuthError(error));
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Regular email/password login
  const login = async (email, password) => {
    try {
      setIsLoading(true);
      setError(null);

      const firebaseUser = await signInWithEmail(email, password);

      // Update last login in Firestore
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      await setDoc(userDocRef, { lastLogin: new Date() }, { merge: true });

      return firebaseUser;
    } catch (error) {
      const errorMessage = parseAuthError(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Google Sign-in
  const loginWithGoogle = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const firebaseUser = await firebaseSignInWithGoogle();

      // If firebaseUser is null, it means redirect was initiated
      if (!firebaseUser) {
        // Keep loading state as redirect is happening
        // Don't call setIsLoading(false) here as the page will redirect
        return null;
      }

      // Update/create user document in Firestore
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      await setDoc(userDocRef, {
        email: firebaseUser.email,
        name: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        role: 'user',
        lastLogin: new Date(),
        createdAt: new Date()
      }, { merge: true });

      setIsLoading(false);
      return firebaseUser;
    } catch (error) {
      const errorMessage = parseAuthError(error);
      setError(errorMessage);
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  // User registration
  const register = async (email, password, name) => {
    try {
      setIsLoading(true);
      setError(null);

      const firebaseUser = await registerWithEmailAndPassword(email, password, name);

      // Create user document in Firestore
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      await setDoc(userDocRef, {
        email: firebaseUser.email,
        name: name,
        role: 'user',
        createdAt: new Date(),
        lastLogin: new Date()
      });

      return firebaseUser;
    } catch (error) {
      const errorMessage = parseAuthError(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      setError(null);
      await resetPassword(email);
    } catch (error) {
      const errorMessage = parseAuthError(error);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Logout
  const logout = async () => {
    try {
      await firebaseLogOut();
      tokenManager.clearTokens();
      setUser(null);
      setIsAuthenticated(false);
      setSessionInfo(null);
      setError(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    sessionInfo,
    error,
    login,
    loginWithGoogle,
    register,
    forgotPassword,
    logout,
    clearError,
    tokenManager
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;